import { useEffect, useState } from 'react'
import { useParams, Link, Navigate } from 'react-router-dom'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import SEO from '../components/SEO'
import { TopNav, Footer } from '../components/PublicLayout'
import { getArticleBySlug, getArticlesSorted, getCategoryLabel, getArticleCategories, getArticlePrimaryCategory } from '../articles/index'

// Article
// -------
// Renders a single article at /articles/<slug>.
//
// Why fetch at runtime instead of bundling the markdown?
//   CRA does not natively support importing .md files as strings.
//   Workarounds require ejecting or adding craco -- not worth it for
//   one feature. Runtime fetch from /public is the standard CRA blog
//   pattern. ~100ms latency per page load -- negligible for long-form
//   content.
//
// Layout strategy:
//   The article body is constrained to ~640px wide. Research on reading
//   ergonomics consistently shows 60-75 characters per line is the
//   comfortable range; wider lines force eye-scanning across long
//   distances and reduce comprehension. The header and CTA can be
//   wider (720-880px) -- they're glanced at, not read top-to-bottom.
//
//   Generous vertical spacing: paragraphs have margin-bottom 1.5em
//   (browser default is ~1em which is too tight for long-form). H2
//   sections have a 3.5rem top margin and a subtle accent line to act
//   as a visual breath between sections.
//
//   The .article-body class is styled via the <style> block below
//   rather than inline style props, because react-markdown renders
//   into elements we don't directly control (e.g. <p> tags inside the
//   markdown body). Styling via a class selector reaches them all
//   without per-element component overrides.

const SITE_URL = 'https://receiptchaser.com'

function formatDate(iso) {
  if (!iso) return ''
  return new Date(iso).toLocaleDateString('en-GB', {
    year: 'numeric', month: 'long', day: 'numeric',
  })
}

function buildArticleJsonLd(article) {
  return {
    '@context':      'https://schema.org',
    '@type':         'Article',
    headline:        article.title,
    description:     article.description,
    datePublished:   article.date,
    dateModified:    article.date,
    author:          { '@type': 'Organization', name: article.author },
    publisher: {
      '@type': 'Organization',
      name:    'Receipt Chaser',
      logo: {
        '@type': 'ImageObject',
        url:     `${SITE_URL}/brand/icon-transparent.png`,
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id':   `${SITE_URL}/articles/${article.slug}`,
    },
  }
}

// Other-articles list. Excludes current, prefers articles sharing at
// least one category, falls back to most-recent. Returns up to 3.
function getRelatedArticles(current) {
  const all = getArticlesSorted().filter(a => a.slug !== current.slug)
  const currentCats = new Set(getArticleCategories(current))
  const sameCat = all.filter(a =>
    getArticleCategories(a).some(c => currentCats.has(c))
  )
  const others = all.filter(a =>
    !getArticleCategories(a).some(c => currentCats.has(c))
  )
  return [...sameCat, ...others].slice(0, 3)
}

// Article body styles. Lives in a <style> block injected once because
// react-markdown renders elements we don't directly control.
function ArticleBodyStyles() {
  return (
    <style>{`
      .article-body {
        font-size: 1.125rem;
        line-height: 1.8;
        color: var(--ink);
      }
      .article-body > * {
        margin-top: 0;
      }
      .article-body p {
        margin-bottom: 1.5em;
      }
      .article-body p:last-child {
        margin-bottom: 0;
      }
      .article-body h2 {
        font-size: 1.65rem;
        line-height: 1.3;
        margin-top: 3.5rem;
        margin-bottom: 1.2rem;
        padding-top: 2rem;
        border-top: 1px solid var(--surface-2);
        color: var(--ink);
      }
      .article-body h2:first-child {
        margin-top: 0;
        padding-top: 0;
        border-top: none;
      }
      .article-body h3 {
        font-size: 1.25rem;
        line-height: 1.35;
        margin-top: 2.4rem;
        margin-bottom: 0.8rem;
        color: var(--ink);
      }
      .article-body strong {
        font-weight: 600;
        color: var(--ink);
      }
      .article-body a {
        color: var(--accent);
        text-decoration: underline;
        text-underline-offset: 3px;
        text-decoration-thickness: 1px;
      }
      .article-body a:hover {
        text-decoration-thickness: 2px;
      }
      .article-body ul,
      .article-body ol {
        margin-bottom: 1.5em;
        padding-left: 1.5em;
      }
      .article-body li {
        margin-bottom: 0.6em;
      }
      .article-body li:last-child {
        margin-bottom: 0;
      }
      .article-body blockquote {
        margin: 2.5rem 0;
        padding: 1.4rem 1.6rem 1.4rem 1.4rem;
        background: linear-gradient(to right, rgba(72, 146, 145, 0.08), rgba(72, 146, 145, 0.02));
        border-left: 4px solid var(--accent);
        border-radius: 6px;
        font-size: 1.02rem;
        color: var(--ink);
        position: relative;
      }
      .article-body blockquote p {
        margin-bottom: 0.8em;
      }
      .article-body blockquote p:last-child {
        margin-bottom: 0;
      }
      /* The first <strong> inside a callout is the label (e.g. "Key rule:").
         Give it more prominence than regular bold body text. */
      .article-body blockquote p:first-child strong:first-child {
        color: var(--accent);
        font-weight: 700;
        font-size: 0.78rem;
        text-transform: uppercase;
        letter-spacing: 0.08em;
        display: block;
        margin-bottom: 0.5rem;
      }
      .article-body code {
        background: var(--surface-2);
        padding: 0.15em 0.4em;
        border-radius: 4px;
        font-size: 0.92em;
        font-family: 'SF Mono', Menlo, Consolas, monospace;
      }
      .article-body pre {
        background: var(--surface-2);
        padding: 1.2rem;
        border-radius: 8px;
        overflow-x: auto;
        margin: 2rem 0;
        font-size: 0.92rem;
      }
      .article-body pre code {
        background: transparent;
        padding: 0;
      }
      .article-body table {
        width: 100%;
        border-collapse: collapse;
        margin: 2rem 0;
        font-size: 0.98rem;
      }
      .article-body th,
      .article-body td {
        padding: 0.75rem 1rem;
        text-align: left;
        border-bottom: 1px solid var(--surface-2);
      }
      .article-body th {
        background: var(--surface-2);
        font-weight: 600;
        font-size: 0.88rem;
        text-transform: uppercase;
        letter-spacing: 0.03em;
        color: var(--ink-muted);
      }
      .article-body tr:last-child td {
        border-bottom: none;
      }
      .article-body img {
        max-width: 100%;
        height: auto;
        border-radius: 8px;
        margin: 2rem 0;
      }
      .article-body hr {
        border: none;
        border-top: 1px solid var(--surface-2);
        margin: 3rem 0;
      }
    `}</style>
  )
}

export default function Article() {
  const { slug } = useParams()
  const article  = getArticleBySlug(slug)

  // Hooks always at top, before any conditional return.
  const [body, setBody]       = useState(null)
  const [loadError, setError] = useState(false)

  useEffect(() => {
    if (!article) return
    let cancelled = false
    fetch(`/articles/${article.slug}.md`)
      .then(res => {
        if (!res.ok) throw new Error('Article body not found')
        return res.text()
      })
      .then(text => {
        if (!cancelled) setBody(text)
      })
      .catch(() => {
        if (!cancelled) setError(true)
      })
    return () => { cancelled = true }
  }, [article])

  if (!article) return <Navigate to="/articles" replace />

  const readingMinutes = body
    ? Math.max(1, Math.round(body.split(/\s+/).filter(Boolean).length / 200))
    : null

  const related = getRelatedArticles(article)
  const primaryCategory = getArticlePrimaryCategory(article)
  const categoryLabel = getCategoryLabel(primaryCategory)

  return (
    <div style={{ background: 'var(--surface)', minHeight: '100vh' }}>
      <SEO
        title={article.title}
        description={article.description}
        path={`/articles/${article.slug}`}
        jsonLd={buildArticleJsonLd(article)}
      />
      <ArticleBodyStyles />
      <TopNav />

      {/* ─── Header ──────────────────────────────────────────────────── */}
      <header style={{ padding: '64px 0 32px' }}>
        <div className="container" style={{ padding: '0 24px', maxWidth: 720 }}>
          <Link
            to="/articles"
            style={{
              fontSize: '0.88rem', color: 'var(--ink-muted)',
              display: 'inline-block', marginBottom: 32,
              textDecoration: 'none',
            }}
          >
            ← All articles
          </Link>

          {categoryLabel && (
            <div style={{
              fontSize: '0.74rem', fontWeight: 700,
              color: 'var(--accent)', textTransform: 'uppercase',
              letterSpacing: '0.08em', marginBottom: 20,
            }}>
              <Link
                to={`/articles?cat=${primaryCategory}`}
                style={{ color: 'inherit', textDecoration: 'none' }}
              >
                {categoryLabel}
              </Link>
            </div>
          )}

          <h1 style={{
            fontSize: 'clamp(2rem, 5vw, 2.75rem)',
            lineHeight: 1.2, marginBottom: 28,
            letterSpacing: '-0.01em',
          }}>
            {article.title}
          </h1>

          <div style={{
            display: 'flex', gap: 12, alignItems: 'center',
            fontSize: '0.9rem', color: 'var(--ink-muted)',
            paddingBottom: 32, borderBottom: '1px solid var(--surface-2)',
          }}>
            <span style={{ fontWeight: 500 }}>{article.author}</span>
            <span style={{ color: 'var(--ink-faint)' }}>·</span>
            <span>{formatDate(article.date)}</span>
            {readingMinutes && (
              <>
                <span style={{ color: 'var(--ink-faint)' }}>·</span>
                <span>{readingMinutes} min read</span>
              </>
            )}
          </div>
        </div>
      </header>

      {/* ─── Body ────────────────────────────────────────────────────── */}
      <article style={{ padding: '48px 0 80px' }}>
        <div className="container" style={{ padding: '0 24px', maxWidth: 640 }}>
          {loadError ? (
            <p style={{ color: 'var(--ink-muted)' }}>
              This article couldn't be loaded. Please try again later.
            </p>
          ) : !body ? (
            <p style={{ color: 'var(--ink-muted)' }}>Loading…</p>
          ) : (
            <div className="article-body">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  // react-markdown's default <a> doesn't add target/rel.
                  // We override here to open external links in a new tab.
                  // "External" means anything starting with http:// or
                  // https:// -- relative links and same-page anchors
                  // stay in the current tab. rel="noopener noreferrer"
                  // is added on external links to prevent the new tab
                  // from accessing window.opener (clickjacking defense).
                  a: ({ href, children, ...props }) => {
                    const isExternal = typeof href === 'string'
                      && /^https?:\/\//i.test(href)
                    if (isExternal) {
                      return (
                        <a
                          href={href}
                          target="_blank"
                          rel="noopener noreferrer"
                          {...props}
                        >
                          {children}
                        </a>
                      )
                    }
                    return <a href={href} {...props}>{children}</a>
                  },
                }}
              >
                {body}
              </ReactMarkdown>
            </div>
          )}
        </div>
      </article>

      {/* ─── Soft CTA ─────────────────────────────────────────────────── */}
      <section style={{ padding: '64px 0', background: 'var(--white)' }}>
        <div className="container" style={{
          padding: '0 24px', maxWidth: 640, textAlign: 'center',
        }}>
          <h3 style={{ marginTop: 0, marginBottom: 16, fontSize: '1.4rem' }}>
            Tired of chasing receipts from clients?
          </h3>
          <p style={{
            color: 'var(--ink-muted)', marginBottom: 28,
            fontSize: '1.02rem', lineHeight: 1.6,
          }}>
            Receipt Chaser turns your accounting data into a clear list
            of receipts your client needs to upload. Free 30-day trial.
          </p>
          <Link to="/signup" className="btn btn-primary btn-lg">
            Start free trial
          </Link>
        </div>
      </section>

      {/* ─── Related ─────────────────────────────────────────────────── */}
      {related.length > 0 && (
        <section style={{ padding: '80px 0' }}>
          <div className="container" style={{ padding: '0 24px', maxWidth: 880 }}>
            <h3 style={{
              fontSize: '0.78rem', fontWeight: 700,
              color: 'var(--accent)', textTransform: 'uppercase',
              letterSpacing: '0.08em', marginTop: 0, marginBottom: 24,
              textAlign: 'center',
            }}>
              More articles
            </h3>
            <ul style={{
              listStyle: 'none', padding: 0, margin: 0,
              display: 'grid',
              gridTemplateColumns: `repeat(auto-fit, minmax(260px, 1fr))`,
              gap: 16,
            }}>
              {related.map(r => (
                <li key={r.slug}>
                  <Link
                    to={`/articles/${r.slug}`}
                    style={{
                      display: 'block', padding: '24px 24px',
                      background: 'var(--white)', borderRadius: 12,
                      border: '1px solid var(--surface-2)',
                      textDecoration: 'none', color: 'inherit',
                      height: '100%',
                      transition: 'border-color 0.15s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent)' }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--surface-2)' }}
                  >
                    <div style={{
                      fontSize: '0.72rem', fontWeight: 700,
                      color: 'var(--accent)', textTransform: 'uppercase',
                      letterSpacing: '0.05em', marginBottom: 10,
                    }}>
                      {getCategoryLabel(getArticlePrimaryCategory(r)) || 'Article'}
                    </div>
                    <div style={{
                      fontWeight: 600, marginBottom: 8, lineHeight: 1.35,
                      fontSize: '1.05rem',
                    }}>
                      {r.title}
                    </div>
                    <div style={{ fontSize: '0.88rem', color: 'var(--ink-muted)', lineHeight: 1.55 }}>
                      {r.description}
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      <Footer />
    </div>
  )
}
